import type { WritableSignal } from '@angular/core';
import type { MatTooltip } from '@angular/material/tooltip';

/** Matches the shipped message-copy action, not the spec's "~2 seconds". */
export const COPIED_FEEDBACK_MS = 1500;

/**
 * How long an action stays in its error state.
 *
 * Longer than the 1500ms success feedback on purpose: a failure is worth
 * reading, and unlike "Copied!" the user has no other signal that nothing
 * happened.
 */
export const ERROR_FEEDBACK_MS = 3000;

/**
 * The transient feedback of an icon action: the tooltip key doubles as the
 * state, shown programmatically for a while and then back to the idle key.
 *
 * One class for the table's copy action and the chart's Copy as image,
 * because the parts that are easy to get wrong — restoring
 * `hideDelay`, clearing the previous timer, returning to the idle key — are
 * exactly the parts that must not diverge between two buttons that can sit in
 * the same answer.
 *
 * The pending revert is tracked rather than fired and forgotten. A second
 * action inside the window would otherwise leave the first timer running, so
 * the feedback reverted early; and a component destroyed inside the window
 * would have its callback touch a torn-down tooltip. After `destroy()`, `show`
 * does nothing: an outcome that arrives after the action is gone is not shown.
 */
export class TooltipFeedback<K extends string> {
  private revertHandle: ReturnType<typeof setTimeout> | undefined;
  private destroyed = false;

  public constructor(
    private readonly key: WritableSignal<K>,
    private readonly idleKey: K,
    private readonly tooltip: () => MatTooltip,
  ) {}

  /** Whether the owning component is gone. */
  public get isDestroyed(): boolean {
    return this.destroyed;
  }

  /** Shows `key` for `durationMs`, then returns to the idle key. */
  public show(key: K, durationMs: number): void {
    if (this.destroyed) {
      return;
    }
    const tooltip = this.tooltip();
    tooltip.hideDelay = durationMs;
    this.key.set(key);
    tooltip.show();

    clearTimeout(this.revertHandle);
    this.revertHandle = setTimeout(() => {
      tooltip.hideDelay = 0;
      tooltip.hide();
      this.key.set(this.idleKey);
    }, durationMs);
  }

  /** Call from the owner's `DestroyRef`: clears the revert and goes quiet. */
  public destroy(): void {
    this.destroyed = true;
    clearTimeout(this.revertHandle);
  }
}
